import { noop } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { usePhoneMenuState, State, ActionDispatchers } from './menuReducer';
import { LinkOptions, QffAuthData, LogoProps } from './types';
import type { FC } from 'react';

export type InteractionInfo = {
  type: string;
  value: string;
};

export type MenuClickInfo = {
  itemText: string;
  url: string | undefined;
}

export type OnInteractionCallback = (info: InteractionInfo) => void;

export interface HeaderProviderProps {
  skipToContentUrl?: string;
  desktopLinks: LinkOptions[];
  phoneLinks: LinkOptions[];
  qffLinks: LinkOptions[];
  qffAuthData: QffAuthData;
  onClickLogin: () => void;
  onClickLogout: () => void;
  onInteraction: OnInteractionCallback;
  onDismissLoginTooltip?: () => void;
  displayLoginTooltip?: boolean;
  queryString?: string;
  universalNavEnabled?: boolean;
  qffLoginEnabled?: boolean;
  homepageUrl?: string;
  homepageLogoSrc?: string;
  homepageLogoLabel?: string;
  LogoItemRenderer?: (props: LogoProps) => JSX.Element;
  arrowDirection?: string[] | string;
}

export const HeaderProvider: FC<HeaderProviderProps> = ({
  children,
  desktopLinks,
  phoneLinks,
  qffAuthData,
  qffLinks,
  onDismissLoginTooltip,
  displayLoginTooltip,
  queryString,
  ...rest
}) => {
  const [state, actions] = usePhoneMenuState();

  const callbacks = useRef(rest);

  useEffect(() => {
    callbacks.current = rest;
  });

  const onClickLogin = useCallback(() => callbacks.current.onClickLogin(), []);
  const onClickLogout = useCallback(
    () => callbacks.current.onClickLogout(),
    []
  );
  const onInteraction = useCallback(
    (info: InteractionInfo) => callbacks.current.onInteraction(info),
    []
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      ...actions,
      desktopLinks,
      phoneLinks,
      qffAuthData,
      qffLinks,
      onClickLogin,
      onClickLogout,
      onInteraction,
      onDismissLoginTooltip,
      displayLoginTooltip,
      queryString,
    }),
    [
      state,
      actions,
      desktopLinks,
      phoneLinks,
      qffAuthData,
      qffLinks,
      onClickLogin,
      onClickLogout,
      onInteraction,
      onDismissLoginTooltip,
      displayLoginTooltip,
      queryString,
    ]
  );

  return (
    <HeaderContext.Provider value={contextValue}>
      {children}
    </HeaderContext.Provider>
  );
};

export type HeaderContextType = HeaderProviderProps & State & ActionDispatchers;

export const HeaderContext = createContext<HeaderContextType>({
  desktopLinks: [],
  phoneLinks: [],
  qffLinks: [],
  qffAuthData: { isAuthenticated: false },
  isNavMenuOpen: false,
  isQffMenuOpen: false,
  toggleNavMenu: noop,
  toggleQffMenu: noop,
  onClickLogin: noop,
  onClickLogout: noop,
  onInteraction: noop,
  onDismissLoginTooltip: noop,
  displayLoginTooltip: true,
  queryString: undefined,
});

export const useHeaderContext = () => useContext(HeaderContext);
